import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAlarms } from "../../hooks/useAlarms";
import { getById as getBureauById } from "../../services/bureauManagement/bureauManagement.service";
import { formatDateTime } from "../../helpers/formatDateTime";
import { formatMessageDates } from "../../helpers/formatMessageDates";
import { DateTimeDisplay, Breadcrumbs } from "../../components/ui";
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";

import type { Alarm } from "../../models";

type BureauAlarmLike = {
    id?: number | string;
    bureauRecipientName?: string;
    alarmIdentifier?: string;
    path?: string;
    bureauAlarmState?: string;
    alarmState?: number;
    lastOccurrenceAt?: string;
    firstOccurrenceAt?: string;
    createdAt?: string;
    localAck?: boolean;
};

const AlarmDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { alarms, loading, error } = useAlarms();
    const [alarm, setAlarm] = useState<Alarm | null>(null);
    const [alarmSource, setAlarmSource] = useState<'local' | 'bureau' | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        status: "",
        priority: 0,
        path: "",
        message: "",
        acknowledged: false
    });

    useEffect(() => {
        if (!id) return;

        let mounted = true;

        const locate = async () => {
            if (alarms) {
                const byId = alarms.find(a => a.properties.id === id);
                if (byId) {
                    if (!mounted) return;
                    setAlarm(byId);
                    setAlarmSource('local');
                    setFormData({
                        name: byId.properties.name,
                        status: byId.properties.status,
                        priority: byId.properties.priority,
                        path: byId.properties.path,
                        message: byId.properties.message,
                        acknowledged: byId.properties.acknowledged
                    });
                    return;
                }
                if (/^\d+$/.test(id)) {
                    const numericIndex = Number.parseInt(id, 10);
                    const byIndex = alarms[numericIndex];
                    if (byIndex) {
                        if (!mounted) return;
                        setAlarm(byIndex);
                        setAlarmSource('local');
                        setFormData({
                            name: byIndex.properties.name,
                            status: byIndex.properties.status,
                            priority: byIndex.properties.priority,
                            path: byIndex.properties.path,
                            message: byIndex.properties.message,
                            acknowledged: byIndex.properties.acknowledged
                        });
                        return;
                    }
                }
            }

            try {
                const res = await getBureauById(id);
                if (!mounted) return;
                if (res?.success && res.data) {
                    const b = res.data as BureauAlarmLike;
                    const displayName = b.bureauRecipientName || b.alarmIdentifier || b.path || `Bureau Alarm ${String(b.id ?? id)}`;
                    const status = b.bureauAlarmState || (b.alarmState === 1 ? 'Active' : 'Inactive') || 'Unknown';
                    const message = b.lastOccurrenceAt
                        ? `Last occurrence: ${formatDateTime(b.lastOccurrenceAt)}`
                        : (b.firstOccurrenceAt ? `First occurrence: ${formatDateTime(b.firstOccurrenceAt)}` : '');
                    const mapped = {
                        properties: {
                            id: String(b.id ?? id),
                            name: displayName,
                            status,
                            priority: 0,
                            path: b.path || b.alarmIdentifier || '',
                            message,
                            date: b.lastOccurrenceAt || b.firstOccurrenceAt || b.createdAt || new Date().toISOString(),
                            acknowledged: !!b.localAck,
                            processingStatus: undefined,
                            alarmStateId: typeof b.alarmState === 'number' ? b.alarmState : undefined,
                            alarmStateName: b.bureauAlarmState ?? undefined,
                            // keep the original bureau payload for full detail view
                            bureau: b,
                        }
                    };
                    setAlarm(mapped);
                    setAlarmSource('bureau');
                    setFormData({
                        name: mapped.properties.name,
                        status: mapped.properties.status,
                        priority: mapped.properties.priority,
                        path: mapped.properties.path,
                        message: mapped.properties.message,
                        acknowledged: mapped.properties.acknowledged
                    });
                    return;
                }
            } catch (e) {
                // ignore
            }

            setAlarm(null);
            setAlarmSource(null);
        };

        void locate();
        return () => { mounted = false; };
    }, [alarms, id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        let processedValue: string | number | boolean = value;
        if (type === 'checkbox') processedValue = (e.target as HTMLInputElement).checked;
        else if (name === 'priority') processedValue = Number.parseInt(value, 10) || 0;
        setFormData(prev => ({ ...prev, [name]: processedValue }));
    };

    const handleSave = () => {
        console.info('Saving alarm data:', formData);
        setIsEditing(false);
        if (alarm) {
            setAlarm({ properties: { ...alarm.properties, ...formData } });
        }
    };

    const handleCancel = () => {
        if (alarm) {
            setFormData({
                name: alarm.properties.name,
                status: alarm.properties.status,
                priority: alarm.properties.priority,
                path: alarm.properties.path,
                message: alarm.properties.message,
                acknowledged: alarm.properties.acknowledged
            });
        }
        setIsEditing(false);
    };

    // Call useBreadcrumbs before any conditional returns (Rules of Hooks)
    const breadcrumbs = useBreadcrumbs({
        alarmId: id,
    });

    if (loading) return <main><div className="container"><div className="page-section"><LoadingSpinner message="Loading alarm…" /></div></div></main>;
    if (error) return <main><div className="container"><div className="page-section">Error loading alarms: {error}</div></div></main>;
    if (!alarm) return (
        <div className="alarm-details page-section">
            <div className="alarm-details__header">
                <button onClick={() => navigate(-1)} className="btn btn--secondary">← Back to Alarms</button>
            </div>
            <div className="alarm-details__content">
                <h2>Alarm Not Found</h2>
                <p>The requested alarm could not be found. It may have been removed or the link is outdated.</p>
            </div>
        </div>
    );

    return (
        <main>
            <div className="container">

                <section className='page'>
                    <h2 className="heading__title heading__title--dashboard">{alarm.properties.name}</h2>
                    <DateTimeDisplay />
                </section>

                <Breadcrumbs items={breadcrumbs} />

                <section className="alarm-content mb-2">

                    <div className="alarm-details">
                        
                        <div className="alarm-details__content">
                            <div className="alarm-details__rows">
                                {/* Row: Name */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Name</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <input name="name" value={formData.name} onChange={handleInputChange} className="form-control" />
                                        ) : (
                                            <div className="form-display">{alarm.properties.name}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Status */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Status</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <select name="status" value={formData.status} onChange={handleInputChange} className="form-control">
                                                <option value="Active">Active</option>
                                                <option value="Inactive">Inactive</option>
                                            </select>
                                        ) : (
                                            <div className="form-display">
                                                {alarm.properties.status}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Priority */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Priority</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <input type="number" name="priority" value={formData.priority} onChange={handleInputChange} className="form-control" />
                                        ) : (
                                            <div className="form-display">{alarm.properties.priority}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Path */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Path</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <input name="path" value={formData.path} onChange={handleInputChange} className="form-control" />
                                        ) : (
                                            <div className="form-display">{alarm.properties.path}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Message */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Message</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <textarea name="message" value={formData.message} onChange={handleInputChange} rows={3} className="form-control" />
                                        ) : (
                                            <div className="form-display">{formatMessageDates(alarm.properties.message)}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Row: Date / Acknowledged */}
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Date & Time</div>
                                    <div className="alarm-details__value">{formatDateTime(alarm.properties.date)}</div>
                                </div>
                                <div className="alarm-details__row">
                                    <div className="alarm-details__label">Acknowledged</div>
                                    <div className="alarm-details__value">
                                        {isEditing ? (
                                            <label className="checkbox-label"><input type="checkbox" name="acknowledged" checked={formData.acknowledged} onChange={handleInputChange} /> <span>Mark as acknowledged</span></label>
                                        ) : (
                                            <div className="form-display">{alarm.properties.acknowledged ? <span className="acknowledged">Acknowledged</span> : <span className="unacknowledged">Not Acknowledged</span>}</div>
                                        )}
                                    </div>
                                </div>

                                {/* Bureau raw payload (if available) - render each field on its own row */}
                                {alarm.properties.bureau && (
                                    <>
                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Bureau ID</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.id}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Alarm Path ID</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.alarmPathId ?? ''}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Alarm Identifier</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.alarmIdentifier}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Site Identifier</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.siteIdentifier}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">CSH Recipient ID</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.bureauRecipientId ?? ''}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">CSH Recipient Name</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.bureauRecipientName}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">CSH System ID</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.bureauId}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">CSH Endpoint</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.bureauEndpoint}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Alarm State</div>
                                            <div className="alarm-details__value">{String(alarm.properties.bureau.alarmState ?? alarm.properties.alarmStateId ?? '')}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">CSH Alarm State</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.bureauAlarmState}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Is Ack Site</div>
                                            <div className="alarm-details__value">{String(alarm.properties.bureau.isAckSite ?? '')}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Local Ack</div>
                                            <div className="alarm-details__value">{String(alarm.properties.bureau.localAck ?? '')}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Is Visible</div>
                                            <div className="alarm-details__value">{String(alarm.properties.bureau.isVisible ?? '')}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Occurrence Count</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.occurrenceCount ?? ''}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">First Occurrence</div>
                                            <div className="alarm-details__value">{formatDateTime(alarm.properties.bureau.firstOccurrenceAt)}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Last Occurrence</div>
                                            <div className="alarm-details__value">{formatDateTime(alarm.properties.bureau.lastOccurrenceAt)}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Created At</div>
                                            <div className="alarm-details__value">{formatDateTime(alarm.properties.bureau.createdAt)}</div>
                                        </div>

                                        <div className="alarm-details__row">
                                            <div className="alarm-details__label">Path</div>
                                            <div className="alarm-details__value">{alarm.properties.bureau.path}</div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="alarm-details__header">
                        
                            <div className="alarm-details__actions">
                                {isEditing ? (
                                    <div className="btn-group">
                                        <button onClick={handleSave} className="btn btn--primary">Save Changes</button>
                                        <button onClick={handleCancel} className="btn btn--secondary">Cancel</button>
                                    </div>
                                ) : (
                                    <div className="btn-group">
                                        <button onClick={() => setIsEditing(true)} className="btn btn--primary">Edit Alarm</button>
                                        <button onClick={() => navigate(-1)} className="btn btn--secondary">Back</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default AlarmDetails;
